import {
  createGoalsWithIds,
  createRelationships,
  createTagsWithIds,
  createTaskWithSubTasks,
  getRelationshipDocs,
  getUser,
  updateGoal,
  updateRelationshipReferences,
} from 'shared/services/backend-api';
import { getLoggableError, getRollbar } from 'shared/services/error-tracking';
import {
  ActionEvents,
  MixpanelService,
  taskTypeToTrackingTaskType,
} from 'shared/services/tracking';
import { IDPrefixes } from 'shared/types/id';
import { MinimalRelationship } from 'shared/types/relationship';
import { TaskActionType } from 'shared/types/task-action';
import { createTaskValidation } from 'shared/utils/create-task-validation';
import { createTaskToNewTask } from 'shared/utils/mappers/create-task-to-new-task';
import { newGoalsValidation } from 'shared/utils/new-goals-validation';
import { newSubTasksValidation } from 'shared/utils/new-sub-task-validation';
import { newTagsValidation } from 'shared/utils/new-tags-validation';
import { createPrefixId } from 'shared/utils/prefix-id';

export const subscribeToElectronTaskCreate = () => {
  const subscribeElectronTask = window.electron?.onTaskCreate;

  if (!subscribeElectronTask || typeof subscribeElectronTask !== 'function') {
    return;
  }

  subscribeElectronTask(async (task, { subTasks, goals, tags }) => {
    try {
      const _task = createTaskValidation(task);
      //  if the task is not valid, stop immediately
      if (!_task) {
        return;
      }

      const _subTasks = subTasks?.length
        ? newSubTasksValidation(subTasks) ?? []
        : [];
      const _goals = goals?.length ? newGoalsValidation(goals) ?? [] : [];
      const _tags = tags?.length ? newTagsValidation(tags) ?? [] : [];

      // prevent task creation when there is no user
      const user = await getUser();
      if (!user) {
        return;
      }

      // first create goals and tags to get the correct id's to set on the task
      const goalToCreate = _goals.find(({ id }) => _task.goalId === id);
      const tagsToCreate = _tags.filter(({ id }) => _task.tagIds?.includes(id));
      const { goals: createdGoals } = goalToCreate
        ? await createGoalsWithIds([{ ...goalToCreate, taskCount: 1 }])
        : { goals: [] };
      const { oldIdToNewIdMap: tagIdMap } =
        await createTagsWithIds(tagsToCreate);

      // process goalId in task
      _task.goalId = createdGoals?.length
        ? createdGoals[0].id ?? null
        : _task.goalId;

      const createdMainTask = await createTaskWithSubTasks(
        createTaskToNewTask(_task),
        _subTasks ?? [],
      );

      // update goal
      if (
        createdMainTask.goalId &&
        createdMainTask.action?.type !== TaskActionType.GoalTimeslot &&
        !createdGoals?.length
      ) {
        await updateGoal({ id: createdMainTask.goalId, taskCount: 1 });
      }

      // update task relationships
      if (_task.tagIds?.length) {
        // prepare all information needed to update the relationships
        const relationships = await getRelationshipDocs();
        const relationshipsToUpdate: MinimalRelationship[] = [];
        const relationshipsToCreate: MinimalRelationship[] = [];

        _task.tagIds.forEach((tagId) => {
          // prep relationship data
          const _tagId = tagIdMap[tagId] ?? tagId;
          const prefixedId = createPrefixId(_tagId, IDPrefixes.Tag);
          let relationship: MinimalRelationship | undefined =
            relationships.find(({ id }) => id === prefixedId);

          const list = relationship
            ? relationshipsToUpdate
            : relationshipsToCreate;

          if (!relationship) {
            relationship = {
              id: prefixedId,
              references: [],
            };
          }

          relationship.references = Array.from(
            new Set([
              ...relationship.references,
              createPrefixId(createdMainTask.id, IDPrefixes.Task),
            ]),
          );
          list.push(relationship);
        });

        // create and update relationships
        if (relationshipsToCreate.length) {
          await createRelationships(relationshipsToCreate);
        }
        if (relationshipsToUpdate.length) {
          await updateRelationshipReferences(relationshipsToUpdate);
        }
      }

      // mixpanel track task creation
      const mixpanelService = new MixpanelService();
      mixpanelService.track(ActionEvents.TaskCreate, {
        type: taskTypeToTrackingTaskType[createdMainTask.type],
        deadline_added: !!createdMainTask.endStrategy?.deadline,
        linked_goal: !!createdMainTask.goalId,
      });
    } catch (error) {
      getRollbar().error('failed to validate task creation', error as Error, {
        ...getLoggableError(error),
        task,
        subTasks,
        goals,
        tags,
      });
      return;
    }
  });
};
